import type { Editor } from "./canvas";

function svgCursor(
  drawing: string,
  hotspotX: number,
  hotspotY: number,
): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><defs><filter id="s" x="-30%" y="-30%" width="170%" height="170%"><feDropShadow dx="1" dy="1" stdDeviation="1" flood-opacity=".42"/></filter></defs><g filter="url(#s)" stroke-linecap="round" stroke-linejoin="round">${drawing}</g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${hotspotX} ${hotspotY}, pointer`;
}

const pen = svgCursor(
  `<path d="M4 28l2.1-7.1L21.8 5.2l5 5L11.1 25.9z" fill="#fff" stroke="#17221c" stroke-width="1.6"/><path d="M6.1 20.9l5 5L4 28z" fill="#d65331" stroke="#17221c" stroke-width="1.4"/><path d="M19.3 7.7l5 5" fill="none" stroke="#17221c" stroke-width="1.4"/>`,
  4,
  28,
);

const highlighter = svgCursor(
  `<path d="M4 27l3.5-7.7L21.2 5.6l5.2 5.2-13.7 13.7z" fill="#fff" stroke="#17221c" stroke-width="1.6"/><path d="M7.5 19.3l5.2 5.2L4 27z" fill="#f3c53f" stroke="#17221c" stroke-width="1.4"/><path d="M19 7.8l5.2 5.2" fill="none" stroke="#17221c" stroke-width="1.4"/>`,
  4,
  27,
);

const eraser = svgCursor(
  `<path d="M4.4 24.4L17.8 7a3 3 0 014.2-.5l4.3 3.3a3 3 0 01.5 4.2L13.4 31z" fill="#fff" stroke="#17221c" stroke-width="1.6"/><path d="M4.4 24.4l6.6-8.6 8.5 6.6-6.1 8H8.8z" fill="#ef9e88" stroke="#17221c" stroke-width="1.4"/>`,
  5,
  25,
);

const ruler = svgCursor(
  `<path d="M4 25.8L22.8 7l5.2 5.2L9.2 31z" fill="#fff" stroke="#17221c" stroke-width="1.6"/><path d="M17.8 12l2.2 2.2M13.8 16l3.2 3.2M9.8 20l2.2 2.2" fill="none" stroke="#517691" stroke-width="1.6"/>`,
  4,
  26,
);

const reviewMarker = svgCursor(
  `<path d="M4 27l3.2-7.4L19.8 7l5.2 5.2-12.6 12.6z" fill="#fff" stroke="#17221c" stroke-width="1.6"/><path d="M7.2 19.6l5.2 5.2L4 27z" fill="#d65331" stroke="#17221c" stroke-width="1.4"/><path d="M18 3.5h10v7h-5.8L19 13v-2.5h-1z" fill="#fff" stroke="#d65331" stroke-width="1.6"/>`,
  4,
  27,
);

const reviewPoint = svgCursor(
  `<path d="M4.5 5.5h21v14h-12L7 25v-5.5H4.5z" fill="#fff" stroke="#17221c" stroke-width="1.6"/><path d="M10 12.5h10M10 16h7" fill="none" stroke="#d65331" stroke-width="1.8"/>`,
  7,
  25,
);

// A precise hotspot plus a small tool badge makes geometry tools distinguishable.
function geometryCursor(path: string) {
  return svgCursor(
    `<path d="M1 8h14M8 1v14" stroke="white" stroke-width="3"/><path d="M1 8h14M8 1v14" stroke="#17221c" stroke-width="1"/><g transform="translate(17 17)" fill="none" stroke="#17221c" stroke-width="1.5">${path}</g>`,
    8,
    8,
  );
}

const toolCursors: Record<string, string> = {
  draw: pen,
  line: geometryCursor('<path d="M0 12L12 0"/>'),
  arrow: geometryCursor('<path d="M0 12L12 0M4 0h8v8"/>'),
  geo: geometryCursor('<rect width="12" height="10"/>'),
  polygon: geometryCursor('<path d="M1 11L3 0L12 4L10 12Z"/>'),
  cloud: geometryCursor(
    '<path d="M2 10C-2 8 0 4 3 4C2 0 7-1 8 3C13 1 15 7 11 8C13 12 6 14 5 10Z"/>',
  ),
  highlight: highlighter,
  eraser,
  "trace-paper": "crosshair",
  note: geometryCursor(
    '<path d="M0 0h12v8l-4 4H0Z" fill="#ffe88a"/><path d="M8 12V8h4"/>',
  ),
  text: "text",
  dimension: geometryCursor('<path d="M0 6h12M2 2v8M10 2v8"/>'),
  area: geometryCursor('<path d="M0 0h12v12H0ZM0 8L8 0M4 12L12 4"/>'),
  angle: geometryCursor('<path d="M1 0v11h12M1 5a6 6 0 016 6"/>'),
  calibrate: ruler,
  pin: reviewPoint,
  "comment-area": reviewMarker,
};

export function cursorForTool(tool: string): string | undefined {
  return toolCursors[tool];
}

export function applyToolCursor(editor: Editor, tool: string) {
  const container = editor.getContainer();
  const cursor = cursorForTool(tool);
  if (cursor) container.style.setProperty("--canvas-cursor-cross", cursor);
  else container.style.removeProperty("--canvas-cursor-cross");
  return () => {
    container.style.removeProperty("--canvas-cursor-cross");
  };
}
